import { VALIDATION_METADATA } from './ValidationMetadata';

const ipv6Regex = new RegExp(
  '^(([0-9a-fA-F]{1,4}:){7}([0-9a-fA-F]{1,4})|' +
    '([0-9a-fA-F]{1,4}:){1,7}:|' +
    '([0-9a-fA-F]{1,4}:){1,6}:([0-9a-fA-F]{1,4})|' +
    '([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|' +
    '([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|' +
    '([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|' +
    '([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|' +
    '[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|' +
    ':((:[0-9a-fA-F]{1,4}){1,7}|:)|' +
    'fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|' +
    '::(ffff(:0{1,4}){0,1}:){0,1}' +
    '((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\\.){3}' +
    '(25[0-5]|(2[0-4]|1?[0-9])?[0-9])|' +
    '([0-9a-fA-F]{1,4}:){1,4}:' +
    '((25[0-5]|(2[0-4]|1?[0-9])?[0-9])\\.){3}' +
    '(25[0-5]|(2[0-4]|1?[0-9])?[0-9]))$'
);

export const PR_IPV6_ADDRESSING_MODE_NOTE_KEYS = {
  static: 'vlanSettings.NoteIpv6ModeStatic',
  prefix_delegation: 'vlanSettings.NoteIpv6ModePrefixDelegation',
  relay: 'vlanSettings.NoteIpv6ModeDhcpv6Relay',
  ndproxy: 'vlanSettings.NoteIpv6ModePrefixSharing'
};

// The Device UI expandIPv6 (DUI:Utils/validates/Validations.js) without input clean-up. The
// Device UI does not trim, so prIpv6ToBigInt uses this core to match it exactly.
// Syntax only, the Device UI `validateIPv6Format` step 1 (the same regex): none of the range or special-use checks.
export const isPrIpv6Syntax = (ipAddress) => typeof ipAddress === 'string' && ipv6Regex.test(ipAddress.toLowerCase());

const expandIPv6Untrimmed = (rawInput) => {
  let input = rawInput;
  let ipv4Tail = null;

  if (input.includes('.')) {
    const lastColon = input.lastIndexOf(':');
    ipv4Tail = input.slice(lastColon + 1);
    input = input.slice(0, lastColon);

    if (input === ':') {
      input = '';
    }
  }

  const parts = input.split('::');
  if (parts.length > 2) return null;

  const left = parts[0] ? parts[0].split(':').filter((segment) => segment !== '') : [];
  const right = parts[1] ? parts[1].split(':').filter((segment) => segment !== '') : [];
  const totalHextets = ipv4Tail ? 6 : 8;
  const missing = totalHextets - (left.length + right.length);

  if (missing < 0) return null;

  const expanded = [...left, ...Array(missing).fill('0'), ...right];

  if (ipv4Tail) {
    const octetStrs = ipv4Tail.split('.');
    const octets = octetStrs.map((octet) => parseInt(octet, 10));

    if (
      octets.length !== 4 ||
      octets.some((octet, index) => Number.isNaN(octet) || octet > 255 || (octetStrs[index].length > 1 && octetStrs[index][0] === '0'))
    ) {
      return null;
    }

    const high = ((octets[0] << 8) | octets[1]).toString(16);
    const low = ((octets[2] << 8) | octets[3]).toString(16);
    expanded.push(high, low);
  }

  return expanded.map((segment) => segment.padStart(4, '0')).join(':');
};

export const expandIPv6 = (ipAddress) => expandIPv6Untrimmed(String(ipAddress || '').trim());

const expandedIpv6ToBigInt = (expandedAddress) =>
  expandedAddress.split(':').reduce((accumulator, segment) => (accumulator << 16n) + BigInt(parseInt(segment, 16)), 0n);

const bigIntToExpandedIpv6 = (value) => {
  const segments = new Array(8);
  let remainingValue = value;

  for (let index = 7; index >= 0; index -= 1) {
    segments[index] = (remainingValue & 0xffffn).toString(16).padStart(4, '0');
    remainingValue >>= 16n;
  }

  return segments.join(':');
};

export const compressExpandedIpv6 = (expandedAddress) => {
  if (!expandedAddress) return '';

  const segments = String(expandedAddress)
    .toLowerCase()
    .split(':')
    .map((segment) => {
      const normalizedSegment = segment.replace(/^0+/, '');
      return normalizedSegment || '0';
    });

  let bestStart = -1;
  let bestLength = 0;
  let currentStart = -1;
  let currentLength = 0;

  segments.forEach((segment, index) => {
    if (segment === '0') {
      if (currentStart === -1) currentStart = index;
      currentLength += 1;
      if (currentLength > bestLength) {
        bestStart = currentStart;
        bestLength = currentLength;
      }
      return;
    }

    currentStart = -1;
    currentLength = 0;
  });

  if (bestLength < 2) {
    return segments.join(':');
  }

  const head = segments.slice(0, bestStart).join(':');
  const tail = segments.slice(bestStart + bestLength).join(':');

  if (!head && !tail) return '::';
  if (!head) return `::${tail}`;
  if (!tail) return `${head}::`;
  return `${head}::${tail}`;
};

export const isLoopbackIpv6 = (ipAddress) => {
  const normalized = expandIPv6(ipAddress);
  if (!normalized) return false;
  return normalized === '0000:0000:0000:0000:0000:0000:0000:0001';
};

export const isUnspecifiedIpv6 = (ipAddress) => {
  const normalized = expandIPv6(ipAddress);
  if (!normalized) return false;
  return normalized === '0000:0000:0000:0000:0000:0000:0000:0000';
};

export const isAssignedPrIpv6Range = (normalizedAddress) => {
  const first = parseInt(String(normalizedAddress || '').split(':')[0], 16);

  if (first >= 0x2000 && first <= 0x3fff) return true;
  if (first >= 0xfc00 && first <= 0xfdff) return true;

  return false;
};

// Device UI parity (`validateIPv6Format`, Utils/validates/Validations.js 373-404): returns true when invalid, false when
// valid. No trim, exactly as the Device UI: `" 2001:db8::1"` is invalid. A form that trims before it validates (the Device UI
// does so at the field) trims at the caller (round-3 R3V-05).
export const validatePrIpv6Format = (ipAddress) => {
  if (!ipAddress || typeof ipAddress !== 'string') return true;
  const normalizedInput = ipAddress.toLowerCase();

  if (!ipv6Regex.test(normalizedInput)) return true;

  const expanded = expandIPv6(normalizedInput);
  if (!expanded) return true;

  const first = parseInt(expanded.split(':')[0], 16);
  if ((first & 0xffc0) === 0xfe80) return true;
  if (/^ff[0-9a-f]{2}:/.test(normalizedInput)) return true;
  if (isLoopbackIpv6(normalizedInput)) return true;
  if (isUnspecifiedIpv6(normalizedInput)) return true;
  if (!isAssignedPrIpv6Range(expanded)) return true;

  return false;
};

export const isValidPrIpv6Address = (ipAddress) => !validatePrIpv6Format(ipAddress);

// Unique local address, fc00::/7 (first group fc00-fdff). For an address that already passed validatePrIpv6Format.
export const isPrIpv6UniqueLocal = (ipAddress) => {
  const expanded = expandIPv6(ipAddress);
  if (!expanded) return false;
  const first = parseInt(expanded.split(':')[0], 16);
  return first >= 0xfc00 && first <= 0xfdff;
};

export const validatePrIpv6Type = (ipAddress) => {
  if (validatePrIpv6Format(ipAddress)) return false;
  const normalizedInput = String(ipAddress).trim().toLowerCase();

  if (normalizedInput.startsWith('fe80:')) return false;
  if (normalizedInput.startsWith('ff')) return false;
  if (normalizedInput === '::1') return false;

  return true;
};

export const getPrIpv6AddressingModeNoteKey = (interfaceType) => PR_IPV6_ADDRESSING_MODE_NOTE_KEYS[interfaceType] || null;

export const validatePrIpv6PrefixLength = (value, options = {}) => {
  const { min = 1, max = 128, required = true } = options;
  const rawValue = value == null ? '' : String(value).trim();

  if (!rawValue) {
    return {
      isValid: !required,
      code: required ? 'required' : null,
      parsedValue: null
    };
  }

  if (!/^[0-9]+$/.test(rawValue)) {
    return {
      isValid: false,
      code: 'range',
      parsedValue: null
    };
  }

  const parsedValue = parseInt(rawValue, 10);
  if (Number.isNaN(parsedValue) || parsedValue < min || parsedValue > max) {
    return {
      isValid: false,
      code: 'range',
      parsedValue: null
    };
  }

  return {
    isValid: true,
    code: null,
    parsedValue
  };
};

export const validatePrIpv6DnsPair = (primaryDns, secondaryDns) => {
  // Device UI `Wan1.jsx` / `VlanSettings.jsx` validatingIpv6DnsAddress: the texts are checked as typed (no trim; `=== ""` is
  // the only empty). A value of only blanks is therefore a format error, not "required" / "no secondary".
  const primary = String(primaryDns ?? '');
  const secondary = String(secondaryDns ?? '');

  if (!primary) {
    return { isValid: false, code: 'primary-required' };
  }

  if (validatePrIpv6Format(primary)) {
    return { isValid: false, code: 'primary-invalid' };
  }

  if (secondary) {
    if (validatePrIpv6Format(secondary)) {
      return { isValid: false, code: 'secondary-invalid' };
    }

    if (primary.toLowerCase() === secondary.toLowerCase()) {
      return { isValid: false, code: 'duplicate' };
    }
  }

  return { isValid: true, code: null };
};

export const normalizePrIpv6NetworkAddress = (ipAddress, prefixLength) => {
  const expandedAddress = expandIPv6(ipAddress);
  const prefixResult = validatePrIpv6PrefixLength(prefixLength, {
    min: 0,
    max: 128,
    required: true
  });

  if (!expandedAddress || !prefixResult.isValid) return '';

  const hostBits = 128 - prefixResult.parsedValue;
  const addressValue = expandedIpv6ToBigInt(expandedAddress);
  const normalizedValue = hostBits === 0 ? addressValue : (addressValue >> BigInt(hostBits)) << BigInt(hostBits);

  return compressExpandedIpv6(bigIntToExpandedIpv6(normalizedValue));
};

// ---------------------------------------------------------------------------------------------
// Phase 2 shared exports (03-Shared-Foundation.md 3.2). Ports of DUI:Utils/ipv6Utils.js at
// 017ef4f3d (ipv6ToBigInt, getIPv6Range, isOverlapping, pdToUnits), every boundary kept.
// ---------------------------------------------------------------------------------------------

// Port of DUI ipv6ToBigInt: null for a non-string, an empty string or an address the expansion
// rejects. Like the Device UI it does not trim, and hextets go through BigInt(`0x...`), so a
// non-hex hextet is null (parseInt would silently accept a partly-hex hextet).
export const prIpv6ToBigInt = (address) => {
  if (!address || typeof address !== 'string') return null;

  try {
    const expanded = expandIPv6Untrimmed(address);
    if (!expanded) return null;
    return expanded.split(':').reduce((accumulator, part) => (accumulator << 16n) + BigInt(`0x${part}`), 0n);
  } catch (error) {
    return null;
  }
};

// Port of DUI getIPv6Range: { network, broadcast } as BigInt, or null. The prefix must be a
// digits-only string or number (after trim) in 0..128.
export const getPrIpv6Range = (address, prefixLength) => {
  const ipInt = prIpv6ToBigInt(address);
  if (ipInt === null) return null;

  const trimmed = prefixLength?.toString().trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const prefix = BigInt(trimmed);
  if (prefix < 0n || prefix > 128n) return null;

  const hostBits = 128n - prefix;
  const mask = ~0n << hostBits;
  const network = ipInt & mask;
  const broadcast = network + ((1n << hostBits) - 1n);

  return { network, broadcast };
};

const isPrIpv6RangeValue = (range) =>
  Boolean(range) && range.network !== null && range.network !== undefined && range.broadcast !== null && range.broadcast !== undefined;

// Port of DUI isOverlapping: false when either range is missing or incomplete.
export const isPrIpv6RangeOverlapping = (a, b) => {
  if (!isPrIpv6RangeValue(a) || !isPrIpv6RangeValue(b)) return false;
  return a.network <= b.broadcast && b.network <= a.broadcast;
};

// Port of DUI pdToUnits: the number of /64 units in a delegated prefix. Kept exactly, quirks
// included: null / undefined / < 0 / > 64 give 0; a numeric string is coerced; a non-numeric
// value gives NaN; no integer check.
export const prIpv6PdToUnits = (pdLen) => {
  if (pdLen === null || pdLen === undefined || pdLen < 0 || pdLen > 64) return 0;
  return Math.pow(2, 64 - pdLen);
};

// -1 / 0 / 1 by numeric value; null when either address is not convertible (prIpv6ToBigInt).
export const comparePrIpv6Addresses = (a, b) => {
  const first = prIpv6ToBigInt(a);
  const second = prIpv6ToBigInt(b);
  if (first === null || second === null) return null;
  if (first === second) return 0;
  return first > second ? 1 : -1;
};

// True when the address has no host bit set after the prefix (= it IS a network address; the
// Device UI isIPv6NetworkAddress has the opposite sense). False for an invalid address or prefix.
// Same answer as comparing normalizePrIpv6NetworkAddress with the compressed address, but never
// throws on a non-hex hextet.
export const isPrIpv6NetworkAddress = (address, prefixLength) => {
  const range = getPrIpv6Range(address, prefixLength);
  if (!range) return false;
  return range.network === prIpv6ToBigInt(address);
};

// True when both addresses are convertible and fall in the same prefix (port of the Device UI
// C2S ipv6AddrsInSameSubnet for any prefix length; the start <= end part of that function is the
// separate comparePrIpv6Addresses check). False when either input or the prefix is invalid, so
// two invalid inputs never compare equal.
export const arePrIpv6InSamePrefix = (a, b, prefixLength) => {
  const first = getPrIpv6Range(a, prefixLength);
  const second = getPrIpv6Range(b, prefixLength);
  if (!first || !second) return false;
  return first.network === second.network;
};

// 'ipv4' when the IPv4 address rule passes, 'ipv6' when isValidPrIpv6Address passes, else null.
export const getPrIpAddressFamily = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (VALIDATION_METADATA.ipAddress.regex.test(trimmed)) return 'ipv4';
  if (isValidPrIpv6Address(trimmed)) return 'ipv6';
  return null;
};

// ---------------------------------------------------------------------------------------------
// R08 NPT / Netmap rules (docs/ipv6-phase2/features/R08-NPT-Netmap.md 4.2, 6.6). Exact Device UI
// rules (Q13 = A, Q-R08-3 = A): DUI:Utils/Modals/AddNptRulesModal.jsx at 017ef4f3d, the limit from
// DUI:Utils/Product.js:263. Contract: Confluence page 1986625792 (last modified 2026-09-22).
// ---------------------------------------------------------------------------------------------

export const PR_NPT_NAME_MAX_LENGTH = 64;
// V-N3: any character outside [A-Za-z0-9_] (AddNptRulesModal.jsx:80). No `g` flag: safe for .test().
export const PR_NPT_NAME_INVALID_CHAR = /[^a-zA-Z0-9_]/;
export const PR_NPT_PREFIX_LENGTH_MIN = 1;
export const PR_NPT_PREFIX_LENGTH_MAX = 64;
// V-N0: ProductPR60X maxProfileNetmap (PR460X inherits it). Default rules are not counted.
export const PR_NPT_MAX_USER_RULES = 64;
// Typing / paste filters (AddNptRulesModal.jsx:394, :405). For String.prototype.replace only:
// never call .test() on these `g` regexes (lastIndex would carry over between calls).
export const PR_NPT_PREFIX_INPUT_FILTER = /[^0-9a-fA-F:]/g;
export const PR_NPT_LENGTH_INPUT_FILTER = /[^0-9]/g;

// Port of DUI normalizeIPv6Address (AddNptRulesModal.jsx:266-285): the network address when the
// length is 1..64, the address is GUA / ULA and the network address is GUA / ULA too; otherwise
// the input unchanged (4.2.8 - this is why /1 keeps the typed prefix).
export const normalizePrNptPrefix = (address, prefixLength) => {
  const trimmed = String(address ?? '').trim();
  if (!trimmed) return address;
  const parsed = parseInt(prefixLength, 10);
  if (parsed >= PR_NPT_PREFIX_LENGTH_MIN && parsed <= PR_NPT_PREFIX_LENGTH_MAX && !validatePrIpv6Format(trimmed)) {
    const network = normalizePrIpv6NetworkAddress(trimmed, parsed);
    if (network && !validatePrIpv6Format(network)) return network;
  }
  return address;
};

// What goes on the wire: the normalised network address, or the RFC 5952 form of the typed
// address when normalisation keeps it (lesson A6). Differs from the Device UI only in letter case
// and zero compression, and only when the typed value is kept.
export const toPrNptWirePrefix = (address, prefixLength) => {
  const value = normalizePrNptPrefix(address, prefixLength);
  // An invalid value (including '') is returned as it is: expandIPv6('') gives all zeros, so an
  // empty value must never become '::'. The validator blocks invalid values before any send.
  if (validatePrIpv6Format(value)) return value;
  const expanded = expandIPv6(value);
  return expanded ? compressExpandedIpv6(expanded) : value;
};

// Count of user rules against PR_NPT_MAX_USER_RULES (NptRules.jsx:251-265). Default rules are
// never passed in: they do not count.
export const isPrNptUserRuleLimitReached = (userRules) => (Array.isArray(userRules) ? userRules.length : 0) >= PR_NPT_MAX_USER_RULES;

const PR_NPT_LENGTH_OPTIONS = { min: PR_NPT_PREFIX_LENGTH_MIN, max: PR_NPT_PREFIX_LENGTH_MAX, required: true };

const getPrNptAddressError = (value, ids) => {
  const text = value == null ? '' : String(value);
  if (text === '') return ids.required;
  if (isLoopbackIpv6(text)) return ids.loopback;
  if (validatePrIpv6Format(text)) return ids.invalid;
  return null;
};

const getPrNptLengthError = (value, ids) => {
  const result = validatePrIpv6PrefixLength(value, PR_NPT_LENGTH_OPTIONS);
  if (result.isValid) return null;
  return result.code === 'required' ? ids.required : ids.range;
};

const prNptRuleList = (rules) => (Array.isArray(rules) ? rules.filter((rule) => rule && typeof rule === 'object') : []);

/**
 * The one NPT form check (rule R14f): its result decides both "show errors" and "send".
 * Returns an object of message ids per field; empty object = valid. Inside one field the first
 * failing Device UI rule wins (4.2): name V-N1..V-N4, source V-N5 / V-N6 / V-N8, destination
 * V-N9 / V-N7 / V-N10 / V-N11 / V-N12, lengths V-N13 / V-N14 and V-N15 / V-N16 / V-N17.
 *
 * @param {object} form - { name, sourcePrefix, sourcePrefixLength, destinationInterface,
 *   destinationPrefix, destinationPrefixLength } as typed (strings).
 * @param {object} context - { mode: 'add' | 'edit', userRules, defaultRules, editedName }.
 */
export const validatePrNptRuleForm = (form = {}, { mode = 'add', userRules = [], defaultRules = [], editedName = null } = {}) => {
  const errors = {};
  const users = prNptRuleList(userRules);
  const defaults = prNptRuleList(defaultRules);
  const isEdit = mode === 'edit';

  // V-N1..V-N4 (AddNptRulesModal.jsx:79-103). Not trimmed. The name list holds every user and
  // default rule name; edit leaves out the edited name (NptRules.jsx:212-215). Case-sensitive.
  const name = form.name == null ? '' : String(form.name);
  const otherNames = [...defaults, ...users].map((rule) => rule.name).filter((ruleName) => !(isEdit && ruleName === editedName));
  if (name === '') errors.name = 'validationPrNptNameRequired';
  else if (name.length > PR_NPT_NAME_MAX_LENGTH) errors.name = 'validationPrNptNameMaxLength';
  // V-N3 is skipped for an unchanged name in edit mode (round-2 finding R08-F4, the one deliberate deviation from the Device UI):
  // the name is read-only on edit, and a rule the device itself holds under a name with `-` (the Confluence example is
  // `npt-lan-to-wan`) could otherwise never be saved. A new or changed name still gets the Device UI rule.
  else if (PR_NPT_NAME_INVALID_CHAR.test(name) && !(isEdit && name === editedName)) errors.name = 'validationPrNptNameInvalidCharacters';
  else if (otherNames.includes(name)) errors.name = 'validationPrNptNameExists';

  const sourcePrefix = form.sourcePrefix == null ? '' : String(form.sourcePrefix);
  const destinationPrefix = form.destinationPrefix == null ? '' : String(form.destinationPrefix);

  const sourceError = getPrNptAddressError(sourcePrefix, {
    required: 'validationPrNptSourcePrefixRequired',
    loopback: 'validationPrNptSourcePrefixLoopback',
    invalid: 'validationPrNptSourcePrefixInvalid'
  });
  if (sourceError) errors.sourcePrefix = sourceError;

  const destinationError = getPrNptAddressError(destinationPrefix, {
    required: 'validationPrNptDestinationPrefixRequired',
    loopback: 'validationPrNptDestinationPrefixLoopback',
    invalid: 'validationPrNptDestinationPrefixInvalid'
  });
  if (destinationError) errors.destinationPrefix = destinationError;

  const sourceLengthError = getPrNptLengthError(form.sourcePrefixLength, {
    required: 'validationPrNptSourcePrefixLengthRequired',
    range: 'validationPrNptSourcePrefixLengthRange'
  });
  if (sourceLengthError) errors.sourcePrefixLength = sourceLengthError;

  const destinationLengthError = getPrNptLengthError(form.destinationPrefixLength, {
    required: 'validationPrNptDestinationPrefixLengthRequired',
    range: 'validationPrNptDestinationPrefixLengthRange'
  });
  if (destinationLengthError) errors.destinationPrefixLength = destinationLengthError;
  else if (!sourceLengthError && parseInt(form.sourcePrefixLength, 10) !== parseInt(form.destinationPrefixLength, 10)) {
    // V-N17 (AddNptRulesModal.jsx:212-218).
    errors.destinationPrefixLength = 'validationPrNptPrefixLengthMismatch';
  }

  if (!sourceError && !destinationError) {
    if (sourcePrefix.toLowerCase() === destinationPrefix.toLowerCase()) {
      // V-N11 on the typed text, before normalisation (AddNptRulesModal.jsx:142-148). FE only.
      errors.destinationPrefix = 'validationPrNptPrefixesSame';
    } else if (!sourceLengthError && !destinationLengthError) {
      // V-N12: equal normalised source and equal normalised destination, against user and default rules; edit skips the
      // edited rule (AddNptRulesModal.jsx:149-182). DOCUMENTED DEVIATION from the Device UI (firmware answer 19,
      // 2026-10-08, live-confirmed): the Device UI also requires the same interface, but the firmware refuses a second
      // rule with the same source AND destination prefix pair whatever the name or the interface (netmap_rule.c, error
      // DAIE_NETMAP_RULE_SOURCE_DEST_IPV6_DUPLICATE), so the interface is not part of the check here.
      const currentSource = String(normalizePrNptPrefix(sourcePrefix, form.sourcePrefixLength) ?? '').toLowerCase();
      const currentDestination = String(normalizePrNptPrefix(destinationPrefix, form.destinationPrefixLength) ?? '').toLowerCase();
      const candidates = [...defaults, ...users.filter((rule) => !(isEdit && rule.name === editedName))];
      const isDuplicate = candidates.some(
        (rule) =>
          String(normalizePrNptPrefix(rule.sourceIpv6Prefix, rule.sourceIpv6Mask) ?? '').toLowerCase() === currentSource &&
          String(normalizePrNptPrefix(rule.destinationIpv6Prefix, rule.destinationIpv6Mask) ?? '').toLowerCase() === currentDestination
      );
      if (isDuplicate) errors.destinationPrefix = 'validationPrNptPrefixPairExists';
    }
  }

  return errors;
};
